import { silentlyRefreshTokens } from './auth0-client';
import config from './config';

/**
 * Token refresh utility for axios interceptors.
 *
 * Strategy (in order):
 * 1. Auth0 SDK — preferred; handles refresh token rotation and always issues
 *    a new id_token (which the Sonio API uses for auth).
 * 2. Manual refresh — fallback for the rare case where the SDK isn't ready
 *    (e.g., the very first requests fired before Authenticated mounts).
 *    If the manual grant doesn't return a new id_token we return null rather
 *    than the stale token, preventing infinite 401 retry loops.
 */

let refreshTokenPromise: Promise<string | null> | null = null;

/** Find Auth0 cache key by scanning localStorage keys. */
const findAuth0CacheKey = (): string | null => {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith('@@auth0spajs@@')) {
      return key;
    }
  }
  return null;
};

/** Read the refresh_token from Auth0's internal localStorage cache. */
const getRefreshTokenFromCache = (): string | null => {
  try {
    const cacheKey = findAuth0CacheKey();
    if (!cacheKey) return null;
    const raw = localStorage.getItem(cacheKey);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return data.body?.refresh_token ?? null;
  } catch {
    return null;
  }
};

export const refreshAccessToken = async (): Promise<string | null> => {
  // Deduplicate concurrent refresh calls
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      // ── Strategy 1: Auth0 SDK ────────────────────────────────────────────
      // Preferred because the SDK properly handles refresh token rotation and
      // always returns a fresh id_token alongside the new access_token.
      const sdkToken = await silentlyRefreshTokens();
      if (sdkToken) {
        return sdkToken;
      }

      // ── Strategy 2: Manual token endpoint ───────────────────────────────
      // Fallback when the SDK isn't initialised yet (rare edge case).
      const idToken = localStorage.getItem('idToken');
      const accessToken = localStorage.getItem('accessToken');
      if (!idToken && !accessToken) return null;

      const refreshToken = getRefreshTokenFromCache();
      if (!refreshToken) return null;

      const response = await fetch(`https://${config.domain}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          client_id: config.client_id,
          refresh_token: refreshToken,
          ...(config.audience && { audience: config.audience }),
        }),
      });

      if (!response.ok) {
        if (response.status === 400 || response.status === 401) {
          // Refresh token is invalid — force re-authentication
          localStorage.removeItem('accessToken');
          localStorage.removeItem('idToken');
        }
        return null;
      }

      const data = await response.json();
      if (!data.access_token) return null;

      localStorage.setItem('accessToken', data.access_token);

      if (data.id_token) {
        localStorage.setItem('idToken', data.id_token);
        // Update Auth0's internal cache so the SDK stays in sync
        const cacheKey = findAuth0CacheKey();
        if (cacheKey) {
          try {
            const raw = localStorage.getItem(cacheKey);
            if (raw) {
              const cacheData = JSON.parse(raw);
              if (cacheData.body) {
                cacheData.body.access_token = data.access_token;
                cacheData.body.id_token = data.id_token;
                if (data.refresh_token) {
                  cacheData.body.refresh_token = data.refresh_token;
                }
                if (data.expires_in) {
                  cacheData.expires_at =
                    Math.floor(Date.now() / 1000) + data.expires_in;
                }
                localStorage.setItem(cacheKey, JSON.stringify(cacheData));
              }
            }
          } catch {
            // Cache update failure is non-fatal
          }
        }
        return data.id_token;
      }

      // The grant succeeded but didn't return a new id_token.
      // Returning the stale idToken here would cause the retry to 401 again,
      // so we return null and let the interceptor reject the request cleanly.
      return null;
    } catch {
      return null;
    } finally {
      // Reset the singleton so future refreshes can proceed
      setTimeout(() => {
        refreshTokenPromise = null;
      }, 1000);
    }
  })();

  return refreshTokenPromise;
};
